(() => {
  if (customElements.get('portrait-configurator')) return;

  const STAGES = Object.freeze({
    photograph: 1,
    frame: 2,
    size: 3,
    creation: 4,
    review: 5,
  });

  const BROWSER_PREVIEW_EXTENSIONS = Object.freeze(['jpg', 'jpeg', 'png', 'webp']);
  const BROWSER_PREVIEW_MIME_TYPES = Object.freeze(['image/jpeg', 'image/png', 'image/webp']);

  class PortraitConfigurator extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.initialized = true;
      this.abortController = new AbortController();
      this.photoValidationToken = 0;
      this.photoObjectUrl = null;
      this.completedSteps = new Set();

      try {
        this.config = JSON.parse(this.querySelector('[data-portrait-config]').textContent);
      } catch (error) {
        this.failInitialization('The portrait configurator could not be loaded.', error);
        return;
      }

      this.cacheElements();
      this.prepareSizeCards();
      this.initializeState();
      this.bindEvents();
      this.setupPreviewResizeObserver();
      this.applyJourney();
      this.render({ announce: false, updateUrl: false });
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.previewResizeObserver?.disconnect();
      this.releasePhotoObjectUrl();
      this.initialized = false;
    }
	
    failInitialization(message, error) {
      console.error('[Portrait configurator]', error);
      const experience = this.querySelector('[data-experience]');
      if (experience) {
        experience.hidden = false;
        experience.innerHTML = `<p role="alert">${message}</p>`;
      }
    }

    cacheElements() {
      this.elements = {
        marketing: this.querySelector('[data-marketing]'),
        experience: this.querySelector('[data-experience]'),
        campaigns: [...this.querySelectorAll('[data-campaign]')],
        form: this.querySelector('[data-portrait-form]'),
        panels: [...this.querySelectorAll('[data-step]')],
        progressButtons: [...this.querySelectorAll('[data-progress-step]')],
        progressCurrentNumber: this.querySelector('[data-progress-current-number]'),
        progressCurrentLabel: this.querySelector('[data-progress-current-label]'),
        frameCards: [...this.querySelectorAll('[data-frame-value]')],
        sizeCards: [...this.querySelectorAll('[data-size-value]')],
        photoInput: this.querySelector('[data-photo-input]'),
        photoError: this.querySelector('[data-photo-error]'),
        photoWarning: this.querySelector('[data-photo-warning]'),
        photoPreview: this.querySelector('[data-photo-preview]'),
        photoPlaceholder: this.querySelector('[data-photo-placeholder]'),
        previewAperture: this.querySelector('[data-preview-aperture]'),
        uploadFilename: this.querySelector('[data-upload-filename]'),
        uploadLabel: this.querySelector('[data-upload-label]'),
        replacePhoto: this.querySelector('[data-replace-photo]'),
        cropControls: this.querySelector('[data-crop-controls]'),
        cropZoom: this.querySelector('[data-crop-zoom]'),
        cropZoomOutput: this.querySelector('[data-crop-zoom-output]'),
        cropPositionOutput: this.querySelector('[data-crop-position-output]'),
        cropCenterX: this.querySelector('[data-crop-center-x]'),
        cropCenterY: this.querySelector('[data-crop-center-y]'),
        cropReset: this.querySelector('[data-crop-reset]'),
        previewStage: this.querySelector('[data-preview-stage]'),
        previewScale: this.querySelector('[data-preview-scale]'),
        previewFrame: this.querySelector('[data-preview-frame]'),
        frameOverlay: this.querySelector('[data-frame-overlay]'),
        variantMedia: this.querySelector('[data-variant-media]'),
        previewFrameName: this.querySelector('[data-preview-frame-name]'),
        previewSizeName: this.querySelector('[data-preview-size-name]'),
        frameError: this.querySelector('[data-frame-error]'),
        sizeError: this.querySelector('[data-size-error]'),
        creationChoices: [...this.querySelectorAll('[data-creation-choice]')],
        creationOrderDate: this.querySelector('[data-creation-order-date]'),
        standardDeliveryDate: this.querySelector('[data-standard-delivery-date]'),
        priorityDeliveryDate: this.querySelector('[data-priority-delivery-date]'),
        priorityPriceLabels: [...this.querySelectorAll('[data-priority-price-label]')],
        priorityNotice: this.querySelector('[data-priority-notice]'),
        variantId: this.querySelector('[data-variant-id]'),
        propertyFrame: this.querySelector('[data-property-frame]'),
        propertySize: this.querySelector('[data-property-size]'),
        propertyExtras: this.querySelector('[data-property-extras]'),
        propertyCreation: this.querySelector('[data-property-creation]'),
        propertyJourney: this.querySelector('[data-property-journey]'),
        propertySegment: this.querySelector('[data-property-segment]'),
        propertyConfiguration: this.querySelector('[data-property-configuration]'),
        propertyCrop: this.querySelector('[data-property-crop]'),
        reviewPhoto: this.querySelector('[data-review-photo]'),
        reviewFilename: this.querySelector('[data-review-filename]'),
        reviewCrop: this.querySelector('[data-review-crop]'),
        reviewFrame: this.querySelector('[data-review-frame]'),
        reviewSize: this.querySelector('[data-review-size]'),
        reviewDimensions: this.querySelector('[data-review-dimensions]'),
        reviewCreation: this.querySelector('[data-review-creation]'),
        reviewCreationDate: this.querySelector('[data-review-creation-date]'),
        reviewBasePrice: this.querySelector('[data-review-base-price]'),
        priorityTotalRow: this.querySelector('[data-priority-total-row]'),
        reviewPriorityPrice: this.querySelector('[data-review-priority-price]'),
        reviewTotal: this.querySelector('[data-review-total]'),
        availability: this.querySelector('[data-availability]'),
        addButton: this.querySelector('[data-add-to-cart]'),
        addLabel: this.querySelector('[data-add-label]'),
        addingLabel: this.querySelector('[data-adding-label]'),
        cartError: this.querySelector('[data-cart-error]'),
        cartSuccess: this.querySelector('[data-cart-success]'),
        liveRegion: this.querySelector('[data-live-region]'),
      };
    }

    prepareSizeCards() {
      const container = this.querySelector('[data-size-options]');
      if (!container || !this.elements.sizeCards.length) return;

      this.elements.sizeCards.sort((a, b) => this.toNumber(a.dataset.sortOrder, 1) - this.toNumber(b.dataset.sortOrder, 1));
      this.elements.sizeCards.forEach((card) => container.appendChild(card));

      this.maximumSizeWidth = Math.max(
        ...this.elements.sizeCards.map((card) => this.toNumber(card.dataset.widthInches, 1)),
        1,
      );

      this.elements.sizeCards.forEach((card) => {
        const ratio = this.toNumber(card.dataset.widthInches, 1) / this.maximumSizeWidth;
        card.style.setProperty('--pc-relative-size', String(Math.max(0.1, Math.min(ratio, 1))));
        card.style.setProperty('--pc-size-visual-border', `${this.clamp(3 / ratio, 3, 12).toFixed(2)}px`);
        card.style.setProperty('--pc-sort-order', card.dataset.sortOrder || '1');
      });
    }

    initializeState() {
      const selectedVariant = this.config.product.variants.find(
        (variant) => String(variant.id) === String(this.config.product.selectedVariantId),
      ) || this.config.product.variants[0] || null;

      const frameIndex = this.config.product.frameOptionIndex;
      const sizeIndex = this.config.product.sizeOptionIndex;
      const extrasIndex = this.config.product.extrasOptionIndex;
      const selectedExtras = selectedVariant && extrasIndex >= 0 ? selectedVariant.options[extrasIndex] : null;
      const selectedExtrasKey = this.extrasKeyFromValue(selectedExtras);
      const url = new URL(window.location.href);
      const requestedJourney = this.normalizeKey(url.searchParams.get('journey'));
      const defaultJourney = this.normalizeKey(this.dataset.defaultJourney) === 'direct' ? 'direct' : 'configurator';
      const journey = ['configurator', 'direct'].includes(requestedJourney) ? requestedJourney : defaultJourney;

      this.state = {
        currentStage: STAGES.photograph,
        maxAccessibleStage: STAGES.photograph,
        photoFile: null,
        photoPreviewUrl: null,
        photoDimensions: null,
        photoValidating: false,
        crop: { x: 50, y: 50, zoom: 1, adjusted: false },
        selectedFrame: selectedVariant && frameIndex >= 0 ? selectedVariant.options[frameIndex] : null,
        selectedSize: selectedVariant && sizeIndex >= 0 ? selectedVariant.options[sizeIndex] : null,
        selectedVariant,
        creationSpeed: selectedExtrasKey === 'express' ? 'priority' : 'standard',
        journey,
        segment: this.normalizeKey(url.searchParams.get('segment')),
        mainPrice: null,
        priorityPrice: null,
        available: Boolean(selectedVariant?.available),
        isSubmitting: false,
      };
    }

    bindEvents() {
      const signal = this.abortController.signal;

      this.querySelectorAll('[data-enter-configurator]').forEach((button) => {
        button.addEventListener('click', () => this.enterConfigurator(), { signal });
      });

      this.elements.photoInput?.addEventListener('change', (event) => this.handlePhotoSelection(event), { signal });
      this.elements.replacePhoto?.addEventListener('click', () => this.elements.photoInput?.click(), { signal });
      this.bindCropEvents(signal);

      this.elements.frameCards.forEach((card) => {
        card.addEventListener('click', () => {
          if (card.disabled) return;
          this.state.selectedFrame = card.dataset.frameValue;
          this.updateSelectedVariant(true);
          this.clearFieldError(this.elements.frameError);
          this.render({ announce: true, updateUrl: true });
        }, { signal });
      });

      this.elements.sizeCards.forEach((card) => {
        card.addEventListener('click', () => {
          if (card.disabled) return;
          const previousTier = this.getSizeTier();
          this.state.selectedSize = card.dataset.sizeValue;
          this.updateSelectedVariant(true);
          this.clearFieldError(this.elements.sizeError);
          this.render({ announce: true, updateUrl: true });
          this.announcePricingChange(previousTier, this.getSizeTier());
        }, { signal });
      });

      this.elements.creationChoices.forEach((input) => {
        input.addEventListener('change', () => {
          this.state.creationSpeed = this.elements.creationChoices.find((choice) => choice.checked)?.value || 'standard';
          this.render({ announce: true, updateUrl: true });
        }, { signal });
      });

      this.querySelectorAll('[data-next-step]').forEach((button) => {
        button.addEventListener('click', () => this.advanceStage(), { signal });
      });

      this.querySelectorAll('[data-previous-step]').forEach((button) => {
        button.addEventListener('click', () => this.goToStage(Math.max(STAGES.photograph, this.state.currentStage - 1)), { signal });
      });

      this.elements.progressButtons.forEach((button) => {
        button.addEventListener('click', () => {
          const stage = this.toNumber(button.dataset.progressStep, 1);
          if (stage <= this.state.maxAccessibleStage) {
            if (stage > this.state.currentStage && this.validateStage(this.state.currentStage, false).valid) {
              this.completedSteps.add(this.state.currentStage);
            }
            this.goToStage(stage);
          }
        }, { signal });
      });

      this.querySelectorAll('[data-edit-step]').forEach((button) => {
        button.addEventListener('click', () => this.goToStage(this.toNumber(button.dataset.editStep, 1)), { signal });
      });

      this.elements.form?.addEventListener('submit', (event) => this.handleSubmit(event), { signal });
      document.addEventListener('shopify:block:select', (event) => this.handleThemeEditorBlockSelect(event), { signal });
    }

    bindCropEvents(signal) {
      const updateFromControls = () => {
        if (!this.cropIsAvailable()) return;
        this.state.crop.zoom = this.clamp(this.toNumber(this.elements.cropZoom?.value, 100) / 100, 1, 2.5);
        this.state.crop.adjusted = true;
        this.renderCropState();
        this.renderFormProperties();
        this.renderReviewCrop();
      };

      this.elements.cropZoom?.addEventListener('input', updateFromControls, { signal });

      this.elements.cropCenterX?.addEventListener('click', () => {
        if (!this.cropIsAvailable()) return;
        this.state.crop.x = 50;
        this.state.crop.adjusted = true;
        this.renderCropState();
        this.renderFormProperties();
        this.renderReviewCrop();
        this.announce('The photograph is centred horizontally.');
      }, { signal });

      this.elements.cropCenterY?.addEventListener('click', () => {
        if (!this.cropIsAvailable()) return;
        this.state.crop.y = 50;
        this.state.crop.adjusted = true;
        this.renderCropState();
        this.renderFormProperties();
        this.renderReviewCrop();
        this.announce('The photograph is centred vertically.');
      }, { signal });

      this.elements.cropReset?.addEventListener('click', () => {
        if (!this.cropIsAvailable()) return;
        this.state.crop = { x: 50, y: 50, zoom: 1, adjusted: false };
        this.renderCropState();
        this.renderFormProperties();
        this.renderReviewCrop();
        this.announce('The photograph crop has been reset to the centre.');
      }, { signal });

      this.elements.previewAperture?.addEventListener('pointerdown', (event) => this.startCropDrag(event), { signal });
      this.elements.previewAperture?.addEventListener('pointermove', (event) => this.moveCropDrag(event), { signal });
      this.elements.previewAperture?.addEventListener('pointerup', (event) => this.endCropDrag(event), { signal });
      this.elements.previewAperture?.addEventListener('pointercancel', (event) => this.endCropDrag(event), { signal });
      this.elements.previewAperture?.addEventListener('keydown', (event) => this.handleCropKeydown(event), { signal });
    }

    setupPreviewResizeObserver() {
      if (!window.ResizeObserver || !this.elements.previewStage) return;
      this.previewResizeObserver = new ResizeObserver(() => {
        window.requestAnimationFrame(() => {
          this.updatePreviewSizeGeometry();
          this.updateFrameGeometry();
          this.updatePhotoLayout();
        });
      });
      this.previewResizeObserver.observe(this.elements.previewStage);
    }

    cropIsAvailable() {
      return Boolean(this.state.photoPreviewUrl && this.state.photoDimensions);
    }

    startCropDrag(event) {
      if (!this.cropIsAvailable() || event.button !== 0) return;
      const layout = this.calculatePhotoLayout();
      if (!layout) return;
      this.cropDrag = {
        pointerId: event.pointerId,
        startClientX: event.clientX,
        startClientY: event.clientY,
        startCropX: this.state.crop.x,
        startCropY: this.state.crop.y,
        overflowX: layout.overflowX,
        overflowY: layout.overflowY,
      };
      this.elements.previewAperture.setPointerCapture?.(event.pointerId);
      this.elements.previewAperture.classList.add('is-dragging');
      event.preventDefault();
    }

    moveCropDrag(event) {
      if (!this.cropDrag || event.pointerId !== this.cropDrag.pointerId) return;
      const deltaX = event.clientX - this.cropDrag.startClientX;
      const deltaY = event.clientY - this.cropDrag.startClientY;
      if (this.cropDrag.overflowX > 0.5) {
        this.state.crop.x = this.clamp(this.cropDrag.startCropX - ((deltaX / this.cropDrag.overflowX) * 100), 0, 100);
      }
      if (this.cropDrag.overflowY > 0.5) {
        this.state.crop.y = this.clamp(this.cropDrag.startCropY - ((deltaY / this.cropDrag.overflowY) * 100), 0, 100);
      }
      this.state.crop.adjusted = true;
      this.renderCropState();
      event.preventDefault();
    }

    endCropDrag(event) {
      if (!this.cropDrag || event.pointerId !== this.cropDrag.pointerId) return;
      this.elements.previewAperture.releasePointerCapture?.(event.pointerId);
      this.elements.previewAperture.classList.remove('is-dragging');
      this.cropDrag = null;
      this.renderFormProperties();
      this.renderReviewCrop();
      this.announce('Photograph position updated.');
    }

    handleCropKeydown(event) {
      if (!this.cropIsAvailable() || !['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) return;
      const increment = event.shiftKey ? 5 : 1;
      if (event.key === 'ArrowLeft') this.state.crop.x = this.clamp(this.state.crop.x + increment, 0, 100);
      if (event.key === 'ArrowRight') this.state.crop.x = this.clamp(this.state.crop.x - increment, 0, 100);
      if (event.key === 'ArrowUp') this.state.crop.y = this.clamp(this.state.crop.y + increment, 0, 100);
      if (event.key === 'ArrowDown') this.state.crop.y = this.clamp(this.state.crop.y - increment, 0, 100);
      this.state.crop.adjusted = true;
      this.renderCropState();
      this.renderFormProperties();
      this.renderReviewCrop();
      event.preventDefault();
    }

    applyJourney() {
      const isMarketingFirst = this.state.journey === 'direct';
      this.elements.marketing.hidden = !isMarketingFirst;
      this.elements.experience.hidden = isMarketingFirst;
      this.selectCampaign();
    }

    selectCampaign(selectedElement = null) {
      const exact = selectedElement || this.elements.campaigns.find(
        (campaign) => this.normalizeKey(campaign.dataset.segment) === this.state.segment && this.state.segment,
      );
      const general = this.elements.campaigns.find((campaign) => this.normalizeKey(campaign.dataset.segment) === 'general');
      const fallback = this.elements.campaigns.find((campaign) => campaign.dataset.segment === '__fallback');
      const selected = exact || general || fallback || this.elements.campaigns[0];

      this.elements.campaigns.forEach((campaign) => {
        campaign.hidden = campaign !== selected;
      });
    }

    enterConfigurator() {
      this.elements.marketing.hidden = true;
      this.elements.experience.hidden = false;
      this.goToStage(STAGES.photograph, { scroll: false });
      this.elements.experience.scrollIntoView({ behavior: this.motionAllowed() ? 'smooth' : 'auto', block: 'start' });
    }

    handleThemeEditorBlockSelect(event) {
      if (!window.Shopify?.designMode) return;
      const eventBlockId = String(event.detail?.blockId || '');
      const block = event.target?.closest?.('[data-campaign], [data-frame-value], [data-size-value]')
        || [...this.querySelectorAll('[data-block-id]')].find((element) => element.dataset.blockId === eventBlockId);
      if (!block || !this.contains(block)) return;

      if (block.matches('[data-campaign]')) {
        this.elements.marketing.hidden = false;
        this.elements.experience.hidden = true;
        this.selectCampaign(block);
      } else {
        this.elements.marketing.hidden = true;
        this.elements.experience.hidden = false;
        this.goToStage(block.matches('[data-frame-value]') ? STAGES.frame : STAGES.size, { force: true, scroll: false });
      }
    }

    async handlePhotoSelection(event) {
      const file = event.target.files?.[0];
      const replacingExistingPhoto = Boolean(this.state.photoFile) || this.state.maxAccessibleStage > STAGES.photograph;
      const token = ++this.photoValidationToken;
      this.clearPhotoMessages();

      if (!file) {
        this.clearPhoto({ preserveProgress: replacingExistingPhoto });
        this.setPhotoError('Choose a photograph before continuing.');
        return;
      }

      this.publishAnalyticsEvent('photo_upload_started', {
        file_type: this.fileExtension(file.name),
      });

      const basicError = this.validateFileBasics(file);
      if (basicError) {
        event.target.value = '';
        this.clearPhoto({ preserveProgress: replacingExistingPhoto });
        this.setPhotoError(basicError);
        return;
      }

      this.releasePhotoObjectUrl();
      this.state.photoFile = null;
      this.state.photoPreviewUrl = null;
      this.state.photoValidating = true;
      this.render({ announce: false, updateUrl: false });

      const objectUrl = URL.createObjectURL(file);

      try {
        const dimensions = await this.readImageDimensions(objectUrl);
        if (token !== this.photoValidationToken) {
          URL.revokeObjectURL(objectUrl);
          return;
        }
        const dimensionError = this.validateImagePixelCount(dimensions);
        if (dimensionError) {
          URL.revokeObjectURL(objectUrl);
          event.target.value = '';
          this.clearPhoto({ preserveProgress: replacingExistingPhoto });
          this.setPhotoError(dimensionError);
          return;
        }
        this.acceptPhoto(file, objectUrl, dimensions);
      } catch (error) {
        if (token !== this.photoValidationToken) {
          URL.revokeObjectURL(objectUrl);
          return;
        }

        URL.revokeObjectURL(objectUrl);
        event.target.value = '';
        this.clearPhoto({ preserveProgress: replacingExistingPhoto });
        this.setPhotoError('This image could not be displayed. Please choose an uncorrupted JPG, PNG or WEBP image so you can review and crop it before ordering.');
      }
    }

    validateFileBasics(file) {
      if (!file.size) return 'The selected photograph is empty. Please choose the original file again.';

      const maximumBytes = this.toNumber(this.config.settings.maxFileSizeMb, 20) * 1024 * 1024;
      if (file.size > maximumBytes) {
        return `The photograph is larger than ${this.config.settings.maxFileSizeMb} MB. Please choose a smaller original file.`;
      }

      const configuredTypes = String(this.config.settings.acceptedFileTypes || '')
        .split(',')
        .map((type) => type.trim().toLowerCase().replace(/^\./, ''))
        .filter(Boolean);
      const accepted = configuredTypes.filter((type) => BROWSER_PREVIEW_EXTENSIONS.includes(type));
      const permittedExtensions = accepted.length ? accepted : BROWSER_PREVIEW_EXTENSIONS;
      const extension = this.fileExtension(file.name);
      const mimeType = String(file.type || '').toLowerCase();
      if (!extension || !permittedExtensions.includes(extension) || (mimeType && !BROWSER_PREVIEW_MIME_TYPES.includes(mimeType))) {
        return 'Choose a browser-compatible JPG, PNG or WEBP image so you can review and crop it before ordering.';
      }

      return '';
    }

    validateImagePixelCount(dimensions) {
      const width = this.toNumber(dimensions?.width, 0);
      const height = this.toNumber(dimensions?.height, 0);
      const maximumPixels = 20_000_000;
      if (!width || !height || width * height <= maximumPixels) return '';

      const megapixels = (width * height / 1_000_000).toFixed(1);
      return `The photograph is ${width} × ${height} pixels (${megapixels} megapixels). The upload supports a maximum of 20 megapixels, so please reduce its pixel dimensions and choose it again.`;
    }

    readImageDimensions(objectUrl) {
      return new Promise((resolve, reject) => {
        const image = new Image();
        image.onload = () => resolve({ width: image.naturalWidth, height: image.naturalHeight });
        image.onerror = () => reject(new Error('Unreadable image'));
        image.src = objectUrl;
      });
    }

    acceptPhoto(file, previewUrl, dimensions) {
      this.releasePhotoObjectUrl();
      this.photoObjectUrl = previewUrl;
      this.state.photoFile = file;
      this.state.photoPreviewUrl = previewUrl;
      this.state.photoDimensions = dimensions;
      this.state.photoValidating = false;
      this.state.crop = { x: 50, y: 50, zoom: 1, adjusted: false };
      this.state.maxAccessibleStage = Math.max(this.state.maxAccessibleStage, STAGES.frame);

      const minimum = this.toNumber(this.config.settings.minimumShortSide, 1200);
      if (dimensions && Math.min(dimensions.width, dimensions.height) < minimum) {
        this.setPhotoWarning(
          `This photograph is ${dimensions.width} × ${dimensions.height} pixels. For the best result, the shortest side should ideally be at least ${minimum} pixels. You may continue or replace it.`,
        );
      }

      this.render({ announce: false, updateUrl: false });
      this.publishAnalyticsEvent('photo_upload_completed', {
        file_type: this.fileExtension(file.name),
        image_width: dimensions?.width || null,
        image_height: dimensions?.height || null,
        browser_preview_available: Boolean(previewUrl),
      });
      this.announce(`${file.name} is ready. Continue to choose a frame.`);
    }

    publishAnalyticsEvent(eventName, eventData = {}) {
      const analytics = window.Shopify?.analytics;
      if (typeof analytics?.publish !== 'function') return;

      const payload = {
        product_id: String(this.config.product.id || ''),
        variant_id: String(this.state.selectedVariant?.id || ''),
        entry_journey: this.state.journey || 'configurator',
        campaign_segment: this.state.segment || null,
        ...eventData,
      };

      try {
        const published = analytics.publish(`marelle:${eventName}`, payload);
        published?.catch?.((error) => console.warn('[Portrait configurator analytics]', error));
      } catch (error) {
        console.warn('[Portrait configurator analytics]', error);
      }
    }

    clearPhoto(options = {}) {
      this.releasePhotoObjectUrl();
      this.state.photoFile = null;
      this.state.photoPreviewUrl = null;
      this.state.photoDimensions = null;
      this.state.photoValidating = false;
      this.state.crop = { x: 50, y: 50, zoom: 1, adjusted: false };
      if (!options.preserveProgress) {
        this.state.maxAccessibleStage = STAGES.photograph;
        this.completedSteps.clear();
        if (this.state.currentStage > STAGES.photograph) this.state.currentStage = STAGES.photograph;
      }
      this.render({ announce: false, updateUrl: false });
    }

    releasePhotoObjectUrl() {
      if (this.photoObjectUrl) URL.revokeObjectURL(this.photoObjectUrl);
      this.photoObjectUrl = null;
    }

    clearPhotoMessages() {
      this.clearFieldError(this.elements.photoError);
      if (this.elements.photoWarning) {
        this.elements.photoWarning.hidden = true;
        this.elements.photoWarning.textContent = '';
      }
    }

    setPhotoError(message) {
      this.setFieldError(this.elements.photoError, message, this.elements.photoInput);
      this.announce(message);
    }

    setPhotoWarning(message) {
      if (!this.elements.photoWarning) return;
      this.elements.photoWarning.textContent = message;
      this.elements.photoWarning.hidden = false;
      this.announce(message);
    }

    advanceStage() {
      const validation = this.validateStage(this.state.currentStage, true);
      if (!validation.valid) {
        validation.focusTarget?.focus();
        this.announce(validation.message);
        return;
      }

      this.completedSteps.add(this.state.currentStage);
      const nextStage = Math.min(STAGES.review, this.state.currentStage + 1);
      this.state.maxAccessibleStage = Math.max(this.state.maxAccessibleStage, nextStage);
      this.goToStage(nextStage);
    }

    goToStage(stage, options = {}) {
      const requestedStage = Math.max(STAGES.photograph, Math.min(STAGES.review, stage));
      if (!options.force && requestedStage > this.state.maxAccessibleStage) return;

      this.state.currentStage = requestedStage;
      if (options.force) this.state.maxAccessibleStage = Math.max(this.state.maxAccessibleStage, requestedStage);
      this.render({ announce: false, updateUrl: false });

      const heading = this.elements.panels.find((panel) => this.toNumber(panel.dataset.step, 1) === requestedStage)
        ?.querySelector('.pc__step-heading');
      heading?.focus({ preventScroll: true });

      if (options.scroll !== false && window.matchMedia('(max-width: 989px)').matches) {
        this.querySelector('.pc__control-column')?.scrollIntoView({
          behavior: this.motionAllowed() ? 'smooth' : 'auto',
          block: 'start',
        });
      }
    }

    updateSelectedVariant(updateUrl) {
      this.state.selectedVariant = this.resolveVariant(this.state.selectedFrame, this.state.selectedSize);
      this.state.available = Boolean(this.state.selectedVariant?.available);
      if (updateUrl && this.state.selectedVariant) this.updateVariantUrl(this.state.selectedVariant.id);
    }

    extrasValue(creationSpeed = this.state.creationSpeed) {
      const values = this.config.settings.extrasValues || {};
      const expressEnabled = creationSpeed === 'priority';
      if (expressEnabled) return values.express;
      return values.none;
    }

    optionValuesEqual(firstValue, secondValue) {
      return this.normalizeOptionValue(firstValue) === this.normalizeOptionValue(secondValue);
    }

    normalizeOptionValue(value) {
      return String(value || '')
        .normalize('NFKC')
        .trim()
        .toLowerCase()
        .replace(/\s+/g, ' ');
    }

    extrasKeyFromValue(value) {
      const normalized = this.normalizeOptionValue(value);
      const configuredValues = this.config.settings.extrasValues || {};
      const configuredMatch = Object.entries(configuredValues).find(
        ([, configuredValue]) => this.normalizeOptionValue(configuredValue) === normalized,
      );
      if (configuredMatch) return configuredMatch[0];

      const compact = normalized.replace(/[^a-z0-9]+/g, '');
      const hasExpress = /^(express|priority)(creation)?$/.test(compact);
      if (hasExpress) return 'express';
      if (/^(none|noextras?|noaddons?|standard|standardcreation)$/.test(compact)) return 'none';
      return null;
    }

    resolveVariant(frameValue, sizeValue, extrasValue = this.extrasValue()) {
      const frameIndex = this.config.product.frameOptionIndex;
      const sizeIndex = this.config.product.sizeOptionIndex;
      const extrasIndex = this.config.product.extrasOptionIndex;
      if (frameIndex < 0 || sizeIndex < 0 || extrasIndex < 0 || !frameValue || !sizeValue || !extrasValue) return null;

      const desiredExtrasKey = this.extrasKeyFromValue(extrasValue);
      return this.config.product.variants.find((variant) => {
        if (!this.optionValuesEqual(variant.options[frameIndex], frameValue)
          || !this.optionValuesEqual(variant.options[sizeIndex], sizeValue)) return false;

        const variantExtrasValue = variant.options[extrasIndex];
        const variantExtrasKey = this.extrasKeyFromValue(variantExtrasValue);
        if (desiredExtrasKey && variantExtrasKey) return variantExtrasKey === desiredExtrasKey;
        return this.optionValuesEqual(variantExtrasValue, extrasValue);
      }) || null;
    }

    variantForSelections(creationSpeed) {
      return this.resolveVariant(
        this.state.selectedFrame,
        this.state.selectedSize,
        this.extrasValue(creationSpeed),
      );
    }

    variantIsPurchasable(variant) {
      return Boolean(variant?.id && variant.available);
    }

    priceDifference(fromVariant, toVariant) {
      if (!fromVariant || !toVariant) return null;
      return this.toNumber(toVariant.price, 0) - this.toNumber(fromVariant.price, 0);
    }

    formatAdjustment(amount) {
      if (amount === null || amount === undefined) return 'Unavailable';
      if (amount === 0) return 'Included';
      if (amount < 0) return `−${this.formatMoney(Math.abs(amount))}`;
      return `+${this.formatMoney(amount)}`;
    }

    updateVariantUrl(variantId) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variantId);
      window.history.replaceState({ ...window.history.state, variantId }, '', url.toString());
    }

    getSizeCard(value = this.state.selectedSize) {
      return this.elements.sizeCards.find((card) => card.dataset.sizeValue === value) || null;
    }

    getFrameCard(value = this.state.selectedFrame) {
      return this.elements.frameCards.find((card) => card.dataset.frameValue === value) || null;
    }

    getSizeTier() {
      const configuredTier = this.getSizeCard()?.dataset.sizeTier;
      if (['petite', 'atelier', 'galerie'].includes(configuredTier)) return configuredTier;

      const normalizedValue = this.normalizeKey(this.state.selectedSize);
      return ['petite', 'atelier', 'galerie'].find((tier) => normalizedValue.includes(tier)) || 'unknown';
    }

    recalculatePrices() {
      const baseVariant = this.variantForSelections('standard');
      this.state.mainPrice = baseVariant?.price ?? null;
      this.state.priorityPrice = this.state.creationSpeed === 'priority'
        ? this.priceDifference(baseVariant, this.state.selectedVariant)
        : 0;
    }

    announcePricingChange(previousTier, currentTier) {
      if (previousTier === currentTier) return;

      const messages = [];
      if (this.state.creationSpeed === 'priority') {
        messages.push(this.state.priorityPrice === null
          ? 'The selected express variant is not offered for this configuration.'
          : this.state.priorityPrice === 0
            ? `${this.config.settings.priorityLabel} is now included.`
            : `${this.config.settings.priorityLabel} now changes the price by ${this.formatAdjustment(this.state.priorityPrice)}.`);
      }
      if (messages.length) this.announce(messages.join(' '));
    }

    render(options = {}) {
      this.updateSelectedVariant(false);
      this.recalculatePrices();
      this.renderJourneyProperties();
      this.renderPanels();
      this.renderProgress();
      this.renderOptionCards();
      this.renderPhotoState();
      this.renderPreview();
      this.renderCropState();
      this.renderCreationState();
      this.renderReview();
      this.renderFormProperties();
      this.renderPurchaseState();

      if (options.updateUrl && this.state.selectedVariant) this.updateVariantUrl(this.state.selectedVariant.id);
      if (options.announce) this.announceVariantState();
    }

    renderJourneyProperties() {
      this.elements.propertyJourney.value = this.state.journey === 'direct' ? 'Marketing first' : 'Configurator first';
      if (this.state.segment) {
        this.elements.propertySegment.disabled = false;
        this.elements.propertySegment.value = this.state.segment;
      } else {
        this.elements.propertySegment.disabled = true;
        this.elements.propertySegment.value = '';
      }
    }

    renderPanels() {
      this.elements.panels.forEach((panel) => {
        panel.hidden = this.toNumber(panel.dataset.step, 1) !== this.state.currentStage;
      });
    }

    renderProgress() {
      this.elements.progressButtons.forEach((button) => {
        const stage = this.toNumber(button.dataset.progressStep, 1);
        const isCurrent = stage === this.state.currentStage;
        const isComplete = this.completedSteps.has(stage);
        button.disabled = stage > this.state.maxAccessibleStage;
        button.classList.toggle('is-complete', isComplete);
        if (isCurrent) button.setAttribute('aria-current', 'step');
        else button.removeAttribute('aria-current');

        const number = button.querySelector('.pc__progress-number');
        if (number) number.textContent = isComplete ? '✓' : String(stage);
      });

      const currentButton = this.elements.progressButtons.find(
        (button) => this.toNumber(button.dataset.progressStep, 1) === this.state.currentStage,
      );
      if (this.elements.progressCurrentNumber) {
        this.elements.progressCurrentNumber.textContent = String(this.state.currentStage);
      }
      if (this.elements.progressCurrentLabel) {
        this.elements.progressCurrentLabel.textContent = currentButton?.dataset.progressName || '';
      }
    }

    renderOptionCards() {
      const extrasValue = this.extrasValue();

      this.elements.frameCards.forEach((card) => {
        const value = card.dataset.frameValue;
        const matchingVariant = this.resolveVariant(value, this.state.selectedSize, extrasValue);
        const available = Boolean(matchingVariant?.available);
        card.disabled = !available;
        card.setAttribute('aria-pressed', String(this.optionValuesEqual(value, this.state.selectedFrame)));
        const status = card.querySelector('[data-choice-availability]');
        if (status) {
          status.textContent = available
            ? ''
            : matchingVariant
              ? 'Currently unavailable'
              : 'Not offered with selected size and extras';
        }
      });

      this.elements.sizeCards.forEach((card) => {
        const value = card.dataset.sizeValue;
        const matchingVariant = this.resolveVariant(this.state.selectedFrame, value, extrasValue);
        const available = Boolean(matchingVariant?.available);
        card.disabled = !available;
        card.setAttribute('aria-pressed', String(this.optionValuesEqual(value, this.state.selectedSize)));
        const price = card.querySelector('[data-size-price]');
        const status = card.querySelector('[data-choice-availability]');
        if (price) price.textContent = matchingVariant ? this.formatMoney(matchingVariant.price) : 'Not offered';
        if (status) {
          status.textContent = available
            ? ''
            : matchingVariant
              ? 'Currently unavailable'
              : 'Not offered with selected frame and extras';
        }
      });
    }

    renderPhotoState() {
      if (!this.elements.uploadFilename || !this.elements.uploadLabel) return;

      if (this.state.photoFile) {
        this.elements.uploadLabel.textContent = 'Photograph ready';
        this.elements.uploadFilename.textContent = `${this.state.photoFile.name} · ${this.formatFileSize(this.state.photoFile.size)}`;
        this.elements.replacePhoto.hidden = false;
      } else {
        const accepted = String(this.config.settings.acceptedFileTypes || '')
          .split(',')
          .map((type) => type.trim().replace(/^\./, '').toUpperCase())
          .filter(Boolean)
          .join(', ');
        this.elements.uploadLabel.textContent = this.state.photoValidating ? 'Checking photograph…' : 'Choose your photograph';
        this.elements.uploadFilename.textContent = accepted;
        this.elements.replacePhoto.hidden = this.state.photoValidating;
      }
    }

    renderPreview() {
      const frameCard = this.getFrameCard();
      const sizeCard = this.getSizeCard();
      const frameName = frameCard?.dataset.frameName || this.state.selectedFrame || '—';
      const sizeName = sizeCard?.dataset.sizeName || this.state.selectedSize || '—';

      this.elements.previewFrameName.textContent = frameName;
      this.elements.previewSizeName.textContent = sizeName;
      this.elements.previewStage.classList.toggle('is-empty', !this.state.photoPreviewUrl);

      if (this.state.photoPreviewUrl) {
        this.elements.photoPreview.src = this.state.photoPreviewUrl;
        this.elements.photoPreview.draggable = false;
        this.elements.photoPreview.hidden = false;
        this.elements.photoPlaceholder.hidden = true;
      } else {
        this.elements.photoPreview.removeAttribute('src');
        this.elements.photoPreview.hidden = true;
        this.elements.photoPlaceholder.hidden = false;
        const placeholderText = this.elements.photoPlaceholder.querySelector('span:last-child');
        if (placeholderText) placeholderText.textContent = this.state.photoFile ? 'Preview unavailable' : 'Your photograph';
      }

      const scale = sizeCard
        ? this.toNumber(sizeCard.dataset.widthInches, 1) / (this.maximumSizeWidth || 1)
        : 0.75;
      this.updatePreviewSizeGeometry(scale);

      const frameColor = frameCard?.dataset.frameColor || '#211d18';
      this.elements.previewFrame.style.setProperty('--pc-frame-color', frameColor);
      this.updateFrameGeometry();

      const overlayUrl = frameCard?.dataset.frameOverlay;
      if (overlayUrl) {
        this.elements.frameOverlay.src = overlayUrl;
        this.elements.frameOverlay.alt = frameCard.dataset.frameOverlayAlt || `${frameName} frame`;
        this.elements.frameOverlay.hidden = false;
        this.elements.variantMedia.hidden = true;
        this.elements.variantMedia.removeAttribute('src');
      } else {
        this.elements.frameOverlay.hidden = true;
        this.elements.frameOverlay.removeAttribute('src');
        const media = this.state.selectedVariant?.featuredMedia;
        if (media?.src) {
          this.elements.variantMedia.src = media.src;
          this.elements.variantMedia.alt = media.alt || `${frameName} product view`;
          this.elements.variantMedia.hidden = false;
        } else {
          this.elements.variantMedia.hidden = true;
          this.elements.variantMedia.removeAttribute('src');
        }
      }
      this.updatePhotoLayout();
    }

    updatePreviewSizeGeometry(scaleOverride = null) {
      const stage = this.elements.previewStage;
      const preview = this.elements.previewScale;
      if (!stage || !preview) return;

      const sizeCard = this.getSizeCard();
      const scale = scaleOverride ?? (sizeCard
        ? this.toNumber(sizeCard.dataset.widthInches, 1) / (this.maximumSizeWidth || 1)
        : 0.75);
      const stageWidth = stage.clientWidth || 0;
      if (!stageWidth) {
        preview.style.removeProperty('width');
        return;
      }

      const styles = window.getComputedStyle?.(stage);
      const paddingLeft = Number.parseFloat(styles?.paddingLeft) || 0;
      const paddingRight = Number.parseFloat(styles?.paddingRight) || 0;
      const padding = paddingLeft + paddingRight;
      const fill = this.clamp(this.toNumber(styles?.getPropertyValue('--pc-preview-fill'), 0.92), 0.4, 1);
      const maximumBaseWidth = this.toNumber(styles?.getPropertyValue('--pc-preview-max-base'), 560);
      const baseWidth = Math.min(Math.max(0, stageWidth - padding) * fill, maximumBaseWidth);
      const selectedWidth = Math.max(96, baseWidth * this.clamp(scale, 0.1, 1));
      preview.style.width = `${selectedWidth.toFixed(2)}px`;
    }

    updateFrameGeometry() {
      const sizeCard = this.getSizeCard();
      const frameCard = this.getFrameCard();
      const sizeWidthCm = this.toNumber(sizeCard?.dataset.widthCm, 0);
      const previewWidth = this.elements.previewScale?.clientWidth || 0;
      const physicalFrameWidthCm = this.toNumber(this.config.settings.frameWidthMm, 20) / 10;
      const fallbackWidth = this.toNumber(frameCard?.dataset.frameWidth, 28);
      const calculatedWidth = sizeWidthCm > 0 && previewWidth > 0
        ? previewWidth * (physicalFrameWidthCm / sizeWidthCm)
        : fallbackWidth;
      this.elements.previewFrame?.style.setProperty('--pc-frame-width', `${this.clamp(calculatedWidth, 6, 64).toFixed(2)}px`);
    }

    calculatePhotoLayout(targetWidth = null, targetHeight = null) {
      if (!this.cropIsAvailable()) return null;
      const apertureWidth = targetWidth || this.elements.previewAperture?.clientWidth || 0;
      const apertureHeight = targetHeight || this.elements.previewAperture?.clientHeight || 0;
      const naturalWidth = this.toNumber(this.state.photoDimensions.width, 0);
      const naturalHeight = this.toNumber(this.state.photoDimensions.height, 0);
      if (!apertureWidth || !apertureHeight || !naturalWidth || !naturalHeight) return null;

      const baseScale = Math.max(apertureWidth / naturalWidth, apertureHeight / naturalHeight);
      const displayScale = baseScale * this.state.crop.zoom;
      const width = naturalWidth * displayScale;
      const height = naturalHeight * displayScale;
      const overflowX = Math.max(0, width - apertureWidth);
      const overflowY = Math.max(0, height - apertureHeight);

      return {
        width,
        height,
        left: -(overflowX * (this.state.crop.x / 100)),
        top: -(overflowY * (this.state.crop.y / 100)),
        overflowX,
        overflowY,
      };
    }

    updatePhotoLayout() {
      if (!this.elements.photoPreview) return;
      const layout = this.calculatePhotoLayout();
      if (!layout) {
        this.elements.photoPreview.removeAttribute('style');
        return;
      }

      this.elements.photoPreview.style.width = `${layout.width}px`;
      this.elements.photoPreview.style.height = `${layout.height}px`;
      this.elements.photoPreview.style.left = `${layout.left}px`;
      this.elements.photoPreview.style.top = `${layout.top}px`;
    }

    renderCropState() {
      const available = this.cropIsAvailable();
      this.elements.cropControls.hidden = !available;
      this.elements.previewAperture.classList.toggle('is-croppable', available);
      this.elements.previewAperture.tabIndex = available ? 0 : -1;
      if (!available) return;

      const zoomPercent = Math.round(this.state.crop.zoom * 100);
      const x = Math.round(this.state.crop.x);
      const y = Math.round(this.state.crop.y);
      this.elements.cropZoom.value = String(zoomPercent);
      this.elements.cropZoomOutput.textContent = `${zoomPercent}%`;
      this.elements.cropPositionOutput.textContent = `Horizontal ${x}% · Vertical ${y}%`;
      this.updatePhotoLayout();
    }

    cropDescription() {
      if (!this.cropIsAvailable()) return 'Original photograph — browser crop preview unavailable';
      if (!this.state.crop.adjusted) return 'Centred crop';
      return `Custom crop — ${Math.round(this.state.crop.zoom * 100)}% zoom, ${Math.round(this.state.crop.x)}% horizontal, ${Math.round(this.state.crop.y)}% vertical`;
    }

    renderReviewCrop() {
      if (this.elements.reviewCrop) this.elements.reviewCrop.textContent = this.cropDescription();
    }

    renderCreationState() {
      const standardVariant = this.variantForSelections('standard');
      const expressVariant = this.variantForSelections('priority');
      const expressAdjustment = this.priceDifference(standardVariant, expressVariant);
      const label = this.variantIsPurchasable(expressVariant)
        ? this.formatAdjustment(expressAdjustment)
        : 'Unavailable';
      this.elements.creationChoices.forEach((choice) => {
        const speed = choice.value === 'priority' ? 'priority' : 'standard';
        const candidate = this.variantForSelections(speed);
        choice.checked = speed === this.state.creationSpeed;
        choice.disabled = !this.variantIsPurchasable(candidate);
      });
      this.elements.priorityPriceLabels.forEach((element) => {
        element.textContent = label;
      });

      if (this.state.creationSpeed !== 'priority') {
        this.elements.priorityNotice.textContent = '';
      } else if (!this.variantIsPurchasable(expressVariant)) {
        this.elements.priorityNotice.textContent = this.config.settings.extrasError;
      } else if (expressAdjustment === 0) {
        this.elements.priorityNotice.textContent = `${this.config.settings.priorityLabel} is included with this portrait variant.`;
      } else {
        this.elements.priorityNotice.textContent = `${this.config.settings.priorityLabel} changes the price by ${this.formatAdjustment(expressAdjustment)}.`;
      }

      this.renderCreationDates();
    }

    deliveryWorkingDays(speed) {
      const timing = this.config.settings.deliveryTiming[speed];
      return timing.creation + timing.production + timing.shipping;
    }

    renderCreationDates() {
      const orderDate = this.localCalendarDate(new Date());
      const standardDate = this.addWorkingDays(orderDate, this.deliveryWorkingDays('standard'));
      const priorityDate = this.addWorkingDays(orderDate, this.deliveryWorkingDays('priority'));
      this.setDateContent(this.elements.creationOrderDate, orderDate);
      this.setDateContent(this.elements.standardDeliveryDate, standardDate);
      this.setDateContent(this.elements.priorityDeliveryDate, priorityDate);

      const selectedDate = this.state.creationSpeed === 'priority' ? priorityDate : standardDate;
      if (this.elements.reviewCreationDate) {
        this.elements.reviewCreationDate.textContent = `Estimated UK delivery: ${this.formatCalendarDate(selectedDate)}`;
      }
    }

    localCalendarDate(date) {
      return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 12);
    }

    addWorkingDays(date, numberOfDays) {
      const result = this.localCalendarDate(date);
      let remaining = Math.max(0, Math.floor(this.toNumber(numberOfDays, 0)));
      while (remaining > 0) {
        result.setDate(result.getDate() + 1);
        const day = result.getDay();
        if (day !== 0 && day !== 6) remaining -= 1;
      }
      return result;
    }

    formatCalendarDate(date) {
      return new Intl.DateTimeFormat('en-GB', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      }).format(date);
    }

    calendarDateValue(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }

    setDateContent(element, date) {
      if (!element) return;
      element.dateTime = this.calendarDateValue(date);
      element.textContent = this.formatCalendarDate(date);
    }

    renderReview() {
      const frameCard = this.getFrameCard();
      const sizeCard = this.getSizeCard();
      const pricingAvailable = this.state.mainPrice !== null
        && this.state.priorityPrice !== null;

      this.elements.reviewPhoto.innerHTML = '';
      if (this.state.photoPreviewUrl) {
        const image = document.createElement('img');
        image.src = this.state.photoPreviewUrl;
        image.alt = '';
        this.elements.reviewPhoto.appendChild(image);
        const reviewLayout = this.calculatePhotoLayout(
          this.elements.reviewPhoto.clientWidth || 54,
          this.elements.reviewPhoto.clientHeight || 68,
        );
        if (reviewLayout) {
          image.style.width = `${reviewLayout.width}px`;
          image.style.height = `${reviewLayout.height}px`;
          image.style.left = `${reviewLayout.left}px`;
          image.style.top = `${reviewLayout.top}px`;
        }
      }

      this.elements.reviewFilename.textContent = this.state.photoFile?.name || 'No photograph selected';
      this.renderReviewCrop();
      this.elements.reviewFrame.textContent = frameCard?.dataset.frameName || this.state.selectedFrame || 'Not selected';
      this.elements.reviewSize.textContent = sizeCard?.dataset.sizeName || this.state.selectedSize || 'Not selected';
      this.elements.reviewDimensions.textContent = sizeCard
        ? `${sizeCard.dataset.widthInches} × ${sizeCard.dataset.heightInches} in · approx. ${sizeCard.dataset.widthCm} × ${sizeCard.dataset.heightCm} cm`
        : '';

      this.elements.reviewCreation.textContent = this.state.creationSpeed === 'priority'
        ? `${this.config.settings.priorityLabel} · ${this.formatAdjustment(this.state.priorityPrice)}`
        : this.config.settings.standardLabel;

      this.elements.reviewBasePrice.textContent = this.state.mainPrice === null ? 'Unavailable' : this.formatMoney(this.state.mainPrice);
      this.elements.priorityTotalRow.hidden = this.state.creationSpeed !== 'priority';
      this.elements.reviewPriorityPrice.textContent = this.formatAdjustment(this.state.priorityPrice);
      this.elements.reviewTotal.textContent = pricingAvailable && this.state.selectedVariant
        ? this.formatMoney(this.state.selectedVariant.price)
        : 'Unavailable';

      if (!this.state.available) {
        this.elements.availability.textContent = this.config.settings.extrasError;
      } else {
        this.elements.availability.textContent = 'This configuration is available.';
      }
      const fullyAvailable = this.state.available && pricingAvailable;
      this.elements.availability.classList.toggle('is-available', fullyAvailable);
      this.elements.availability.classList.toggle('is-unavailable', !fullyAvailable);
    }

    renderFormProperties() {
      this.elements.variantId.value = this.state.selectedVariant?.id || '';
      this.elements.propertyFrame.value = this.state.selectedFrame || '';
      this.elements.propertySize.value = this.state.selectedSize || '';
      this.elements.propertyExtras.value = this.extrasValue() || '';
      this.elements.propertyCrop.value = this.cropDescription();

      this.elements.propertyCreation.value = this.state.creationSpeed === 'priority'
        ? this.config.settings.priorityLabel
        : this.config.settings.standardLabel;
    }

    renderPurchaseState() {
      const validation = this.validateFullConfiguration(false);
      this.elements.addButton.disabled = !validation.valid || this.state.isSubmitting;
      this.elements.addButton.setAttribute('aria-disabled', String(!validation.valid || this.state.isSubmitting));

      const photoNext = this.querySelector('[data-step="1"] [data-next-step]');
      if (photoNext) photoNext.disabled = !this.state.photoFile;
    }

    validateStage(stage, showErrors) {
      if (stage === STAGES.photograph && !this.state.photoFile) {
        const message = 'Choose a valid photograph before continuing.';
        if (showErrors) this.setPhotoError(message);
        return { valid: false, message, focusTarget: this.elements.photoInput };
      }

      if (stage === STAGES.frame) {
        if (!this.state.selectedFrame || !this.state.selectedVariant || !this.state.selectedVariant.available) {
          const message = 'Choose an available frame for the selected size.';
          if (showErrors) this.setFieldError(this.elements.frameError, message);
          return { valid: false, message, focusTarget: this.elements.frameCards.find((card) => !card.disabled) };
        }
      }

      if (stage === STAGES.size) {
        if (!this.state.selectedSize || !this.state.selectedVariant || !this.state.selectedVariant.available) {
          const message = 'Choose an available portrait size.';
          if (showErrors) this.setFieldError(this.elements.sizeError, message);
          return { valid: false, message, focusTarget: this.elements.sizeCards.find((card) => !card.disabled) };
        }
        if (this.getSizeTier() === 'unknown') {
          const message = 'This size needs a Petite, Atelier or Galerie pricing tier in the theme editor.';
          if (showErrors) this.setFieldError(this.elements.sizeError, message);
          return { valid: false, message, focusTarget: this.getSizeCard() };
        }
      }

      if (stage === STAGES.creation && !this.variantIsPurchasable(this.state.selectedVariant)) {
        const message = this.config.settings.extrasError;
        return { valid: false, message, focusTarget: this.elements.creationChoices.find((choice) => !choice.disabled) };
      }

      return { valid: true, message: '' };
    }

    validateFullConfiguration(showErrors) {
      if (!this.config.product.optionsValid) {
        return { valid: false, message: 'The configured Frame, Size or Extras options do not match this product.' };
      }

      for (let stage = STAGES.photograph; stage <= STAGES.creation; stage += 1) {
        const result = this.validateStage(stage, showErrors);
        if (!result.valid) return result;
      }

      if (!this.state.selectedVariant?.available) {
        return { valid: false, message: 'The selected portrait variant is unavailable.' };
      }

      return { valid: true, message: '' };
    }

    setFieldError(element, message, input = null) {
      if (element) {
        element.textContent = message;
        element.hidden = false;
      }
      input?.setAttribute('aria-invalid', 'true');
    }

    clearFieldError(element, input = null) {
      if (element) {
        element.textContent = '';
        element.hidden = true;
      }
      input?.removeAttribute('aria-invalid');
    }

    announceVariantState() {
      if (!this.state.selectedVariant) {
        this.announce('That frame-and-size combination is not offered.');
        return;
      }
      const availability = this.state.selectedVariant.available ? 'available' : 'unavailable';
      this.announce(`${this.state.selectedFrame}, ${this.state.selectedSize}, ${this.extrasValue()}, ${this.formatMoney(this.state.selectedVariant.price)}, ${availability}.`);
    }

    announce(message) {
      if (!this.elements.liveRegion) return;
      this.elements.liveRegion.textContent = '';
      window.requestAnimationFrame(() => {
        this.elements.liveRegion.textContent = message;
      });
    }

    async handleSubmit(event) {
      event.preventDefault();
      if (this.state.isSubmitting) return;

      const validation = this.validateFullConfiguration(true);
      if (!validation.valid) {
        this.showCartError(validation.message);
        validation.focusTarget?.focus();
        return;
      }

      const configurationId = this.createConfigurationId();
      this.elements.propertyConfiguration.value = configurationId;
      this.state.isSubmitting = true;
      this.setSubmittingState(true);
      this.hideCartMessages();

      try {
        const cartDrawer = this.getCompatibleCartDrawer();
        const wantsDrawer = Boolean(cartDrawer) && this.config.settings.enableCartDrawer && !this.config.settings.redirectToCart;
        const sections = wantsDrawer ? cartDrawer.getSectionsToRender().map((section) => section.id) : [];

        const mainFormData = await this.buildMainFormData(configurationId);
        if (wantsDrawer) this.appendSectionRequest(mainFormData, sections);
        const mainItem = await this.addCartItem(mainFormData, 'portrait');
        if (!mainItem?.key) throw new Error('The portrait could not be added because the cart response was incomplete. Please try again.');

        this.dispatchCartEvents(mainItem);

        if (wantsDrawer && mainItem.sections) {
          try {
            cartDrawer.setActiveElement?.(this.elements.addButton);
            cartDrawer.renderContents(mainItem);
            this.elements.cartSuccess.hidden = false;
            this.announce(this.config.settings.successMessage);
          } catch (drawerError) {
            console.error('[Portrait configurator drawer]', drawerError);
            window.location.assign(this.cartUrl());
            return;
          }
        } else {
          window.location.assign(this.cartUrl());
          return;
        }
      } catch (error) {
        console.error('[Portrait configurator cart]', error);
        const message = error.message || 'The portrait could not be added. Please try again.';
        this.showCartError(message);
      } finally {
        this.state.isSubmitting = false;
        this.setSubmittingState(false);
        this.renderPurchaseState();
      }
    }

    async buildMainFormData(configurationId) {
      this.renderFormProperties();
      const formData = new FormData(this.elements.form);
      [...new Set([...formData.keys()])].forEach((key) => {
        if (key.startsWith('portrait_creation_')) formData.delete(key);
      });
      formData.set('id', String(this.state.selectedVariant.id));
      formData.set('quantity', '1');
      formData.set('properties[_configuration_id]', configurationId);

      if (this.cropIsAvailable()) {
        const cropReference = await this.createCropReferenceBlob();
        const baseName = String(this.state.photoFile?.name || 'portrait')
          .replace(/\.[^.]+$/, '')
          .replace(/[^a-z0-9_-]+/gi, '-')
          .replace(/^-+|-+$/g, '')
          .slice(0, 60) || 'portrait';
        formData.set('properties[Crop reference]', cropReference, `${baseName}-crop-reference.jpg`);
        formData.set('properties[_crop_x]', String(Math.round(this.state.crop.x)));
        formData.set('properties[_crop_y]', String(Math.round(this.state.crop.y)));
        formData.set('properties[_crop_zoom]', String(Math.round(this.state.crop.zoom * 100)));
      }

      return formData;
    }

    async createCropReferenceBlob() {
      if (!this.cropIsAvailable()) throw new Error('A crop reference cannot be created for this photograph.');
      const image = await new Promise((resolve, reject) => {
        const previewImage = new Image();
        previewImage.onload = () => resolve(previewImage);
        previewImage.onerror = () => reject(new Error('The crop reference could not read the uploaded photograph.'));
        previewImage.src = this.state.photoPreviewUrl;
      });

      const width = 900;
      const height = 1200;
      const layout = this.calculatePhotoLayout(width, height);
      if (!layout) throw new Error('The crop reference could not be calculated.');

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const context = canvas.getContext('2d');
      if (!context) throw new Error('This browser cannot create the crop reference.');
      context.fillStyle = '#fffaf2';
      context.fillRect(0, 0, width, height);
      context.drawImage(image, layout.left, layout.top, layout.width, layout.height);

      return new Promise((resolve, reject) => {
        canvas.toBlob((blob) => {
          if (blob) resolve(blob);
          else reject(new Error('The crop reference could not be prepared.'));
        }, 'image/jpeg', 0.9);
      });
    }

    appendSectionRequest(formData, sectionIds) {
      if (!sectionIds.length) return;
      formData.set('sections', sectionIds.join(','));
      formData.set('sections_url', window.location.pathname);
    }

    async addCartItem(formData, itemType = 'item') {
      const endpoint = this.cartAddUrl();
      const variantId = String(formData.get('id') || 'unknown');
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: formData,
      });
      const responseText = await response.text();
      let data = {};
      try {
        data = responseText ? JSON.parse(responseText) : {};
      } catch (parseError) {
        console.error('[Portrait configurator cart response]', {
          status: response.status,
          endpoint,
          itemType,
          variantId,
          contentType: response.headers.get('content-type'),
          parseError,
        });
      }
      if (!response.ok || data.status) {
        const platformMessage = String(data.description || data.message || '').replace(/\bShopify\b/gi, 'the store');
        const label = itemType === 'portrait' ? 'portrait' : 'item';
        const message = platformMessage
          ? `The ${label} could not be added: ${platformMessage}`
          : `The ${label} could not be added (HTTP ${response.status}). Please refresh and try again. If the problem continues, contact support.`;
        const cartError = new Error(message);
        cartError.status = response.status;
        cartError.itemType = itemType;
        cartError.variantId = variantId;
        throw cartError;
      }
      return data;
    }

    getCompatibleCartDrawer() {
      const drawer = document.querySelector('cart-drawer');
      return drawer && typeof drawer.getSectionsToRender === 'function' && typeof drawer.renderContents === 'function'
        ? drawer
        : null;
    }

    dispatchCartEvents(cartData) {
      document.dispatchEvent(new CustomEvent('cart:add', { detail: { source: 'portrait-configurator', cartData } }));
      document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { source: 'portrait-configurator' } }));
      if (typeof window.publish === 'function') {
        window.publish('cart-update', {
          source: 'portrait-configurator',
          productVariantId: String(this.state.selectedVariant.id),
          cartData,
        });
      }
    }

    setSubmittingState(submitting) {
      this.elements.addButton.disabled = submitting;
      this.elements.addButton.setAttribute('aria-busy', String(submitting));
      this.elements.addLabel.hidden = submitting;
      this.elements.addingLabel.hidden = !submitting;
    }

    hideCartMessages() {
      this.elements.cartError.hidden = true;
      this.elements.cartError.textContent = '';
      this.elements.cartSuccess.hidden = true;
    }

    showCartError(message) {
      this.elements.cartError.textContent = message;
      this.elements.cartError.hidden = false;
      this.elements.cartError.focus?.();
      this.announce(message);
    }

    createConfigurationId() {
      if (window.crypto?.randomUUID) return window.crypto.randomUUID();
      return `portrait-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 11)}`;
    }

    rootUrl() {
      const root = window.Shopify?.routes?.root || '/';
      return root.endsWith('/') ? root : `${root}/`;
    }

    cartAddUrl() {
      return `${this.rootUrl()}cart/add.js`;
    }

    cartUrl() {
      return window.routes?.cart_url || `${this.rootUrl()}cart`;
    }

    formatMoney(cents) {
      const format = String(this.config.shop.moneyFormat || '{{amount}}').replace(/<[^>]*>/g, '');
      const placeholder = format.match(/\{\{\s*(\w+)\s*\}\}/);
      if (!placeholder) {
        return new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency: this.config.shop.currency,
        }).format(this.toNumber(cents, 0) / 100);
      }

      const formats = {
        amount: [2, ',', '.'],
        amount_no_decimals: [0, ',', '.'],
        amount_with_comma_separator: [2, '.', ','],
        amount_no_decimals_with_comma_separator: [0, '.', ','],
        amount_with_apostrophe_separator: [2, "'", '.'],
        amount_no_decimals_with_space_separator: [0, ' ', ','],
        amount_with_space_separator: [2, ' ', ','],
      };
      const [precision, thousands, decimal] = formats[placeholder[1]] || formats.amount;
      const value = this.amountWithDelimiters(cents, precision, thousands, decimal);
      return format.replace(placeholder[0], value).replace(/\s+/g, ' ').trim();
    }

    amountWithDelimiters(cents, precision, thousands, decimal) {
      const number = (this.toNumber(cents, 0) / 100).toFixed(precision);
      const parts = number.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return parts.length === 2 ? `${parts[0]}${decimal}${parts[1]}` : parts[0];
    }

    fileExtension(filename) {
      return String(filename || '').split('.').pop().toLowerCase();
    }

    formatFileSize(bytes) {
      const megabytes = this.toNumber(bytes, 0) / (1024 * 1024);
      if (megabytes >= 1) return `${megabytes.toFixed(megabytes >= 10 ? 0 : 1)} MB`;
      return `${Math.max(1, Math.round(this.toNumber(bytes, 0) / 1024))} KB`;
    }

    normalizeKey(value) {
      return String(value || '').trim().toLowerCase();
    }

    toNumber(value, fallback = 0) {
      const number = Number(value);
      return Number.isFinite(number) ? number : fallback;
    }

    clamp(value, minimum, maximum) {
      return Math.min(Math.max(value, minimum), maximum);
    }

    motionAllowed() {
      return this.dataset.transitions === 'true' && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }
  }

  customElements.define('portrait-configurator', PortraitConfigurator);
})();
